// src/pages/Hierarchy/JumuiaPage.js
import React, { useEffect, useMemo, useState } from "react";
import { getCurrentUser, isParokiaAdmin as checkParokiaAdmin } from "../../services/auth";
import {
  getJimbos,
  getParokia,
  getMyParokia,
  getKanda,
  getJumuia,
  createJumuia,
  updateJumuia,
} from "../../services/api";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./hierarchy.css";

export default function JumuiaPage() {
  const user = getCurrentUser();
  const isParokiaAdmin = checkParokiaAdmin();

  const [jimbos, setJimbos] = useState([]);
  const [jimbosLoading, setJimbosLoading] = useState(false);

  const [selectedJimbo, setSelectedJimbo] = useState("");
  const [parokiaList, setParokiaList] = useState([]);
  const [parokiaLoading, setParokiaLoading] = useState(false);

  const [selectedParokia, setSelectedParokia] = useState(
    isParokiaAdmin ? String(user?.parokiaId || "") : ""
  );

  const [kandaList, setKandaList] = useState([]);
  const [kandaLoading, setKandaLoading] = useState(false);
  const [selectedKanda, setSelectedKanda] = useState("");

  const [jumuiaList, setJumuiaList] = useState([]);
  const [loading, setLoading] = useState(false);

  const [context, setContext] = useState({
    jimbo_name: "",
    parokia_name: "",
    jimbo_id: null,
    parokia_id: null,
  });

  const [createOpen, setCreateOpen] = useState(false);
  const [viewOpen, setViewOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);

  const initialForm = useMemo(
    () => ({
      name: "",
      leaderName: "",
      mwenyekiti: "",
      katibu: "",
      mweka_hazina: "",
      status: "ACTIVE",
    }),
    []
  );

  const [form, setForm] = useState(initialForm);
  const [selectedJumuia, setSelectedJumuia] = useState(null);

  const formatDate = (dt) => {
    if (!dt) return "-";
    const d = new Date(dt);
    return d.toLocaleString();
  };

  const selectedParokiaObj = useMemo(() => {
    if (!selectedParokia) return null;
    return (
      parokiaList.find((p) => String(p.parokia_id) === String(selectedParokia)) ||
      null
    );
  }, [parokiaList, selectedParokia]);

  const selectedKandaObj = useMemo(() => {
    if (!selectedKanda) return null;
    return (
      kandaList.find((k) => String(k.kanda_id) === String(selectedKanda)) || null
    );
  }, [kandaList, selectedKanda]);

  const viewContext = useMemo(() => {
    if (!isParokiaAdmin) {
      return {
        jimbo_name: selectedParokiaObj?.jimbo_name || "",
        parokia_name: selectedParokiaObj?.parokia_name || "",
      };
    }

    return {
      jimbo_name: context.jimbo_name || "",
      parokia_name: context.parokia_name || user?.parokiaName || "",
    };
  }, [isParokiaAdmin, selectedParokiaObj, context, user]);

  const FieldLabel = ({ children }) => (
    <div
      style={{
        fontSize: 12,
        fontWeight: 600,
        marginBottom: 6,
        color: "#374151",
      }}
    >
      {children}
    </div>
  );

  useEffect(() => {
    if (!isParokiaAdmin) {
      loadJimbos();
    } else {
      const pid = user?.parokiaId;
      if (!pid) {
        toast.error("No parokia assigned to this user");
        return;
      }
      loadMyParokiaUsingUser(pid);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadJimbos = async () => {
    setJimbosLoading(true);
    try {
      const res = await getJimbos();
      setJimbos(Array.isArray(res) ? res : res.data || []);
    } catch (err) {
      console.error("Failed to load Jimbos", err);
      toast.error("Failed to load Jimbos");
      setJimbos([]);
    } finally {
      setJimbosLoading(false);
    }
  };

  const loadMyParokiaUsingUser = async (pidFromUser) => {
    try {
      const res = await getMyParokia();
      const list = res.data || [];

      if (!Array.isArray(list) || list.length === 0) {
        setParokiaList([
          {
            parokia_id: pidFromUser,
            parokia_name: user?.parokiaName || "My Parokia",
          },
        ]);
      } else {
        setParokiaList(list);
      }

      setSelectedParokia(String(pidFromUser));
      await loadKanda(String(pidFromUser));
      setSelectedKanda("");
      setJumuiaList([]);
    } catch (err) {
      console.error("Failed to load my parokia", err);
      setParokiaList([
        {
          parokia_id: pidFromUser,
          parokia_name: user?.parokiaName || "My Parokia",
        },
      ]);
      setSelectedParokia(String(pidFromUser));
      await loadKanda(String(pidFromUser));
      setSelectedKanda("");
      setJumuiaList([]);
    }
  };

  const loadParokiaByJimbo = async (jimboId) => {
    if (!jimboId) {
      setParokiaList([]);
      setSelectedParokia("");
      setKandaList([]);
      setSelectedKanda("");
      setJumuiaList([]);
      return;
    }

    setParokiaLoading(true);
    try {
      const res = await getParokia(jimboId);
      const list = res.data || [];
      setParokiaList(Array.isArray(list) ? list : []);
      setSelectedParokia("");
      setKandaList([]);
      setSelectedKanda("");
      setJumuiaList([]);
    } catch (err) {
      console.error("Failed to load parokia by jimbo", err);
      toast.error("Failed to load Parokia list");
      setParokiaList([]);
      setSelectedParokia("");
      setKandaList([]);
      setSelectedKanda("");
      setJumuiaList([]);
    } finally {
      setParokiaLoading(false);
    }
  };

  const loadKanda = async (parokiaId) => {
    const effectiveParokiaId = isParokiaAdmin ? user?.parokiaId : parokiaId;

    if (!effectiveParokiaId) {
      setKandaList([]);
      setSelectedKanda("");
      return;
    }

    setKandaLoading(true);
    try {
      const res = await getKanda(effectiveParokiaId);
      const list = res.data || [];
      setKandaList(list);

      if (Array.isArray(list) && list.length > 0) {
        const first = list[0];
        setContext({
          jimbo_name: first.jimbo_name || "",
          parokia_name: first.parokia_name || user?.parokiaName || "",
          jimbo_id: first.jimbo_id ?? null,
          parokia_id: first.parokia_id ?? effectiveParokiaId ?? null,
        });
      } else {
        setContext((prev) => ({
          ...prev,
          parokia_id: Number(effectiveParokiaId) || prev.parokia_id,
          parokia_name: user?.parokiaName || prev.parokia_name,
        }));
      }
    } catch (err) {
      console.error("Failed to load Kanda", err);
      toast.error("Failed to load Kanda");
      setKandaList([]);
    } finally {
      setKandaLoading(false);
    }
  };

  const loadJumuiaList = async ({ parokiaId, kandaId }) => {
    const effectiveParokiaId = isParokiaAdmin ? user?.parokiaId : parokiaId;

    if (!effectiveParokiaId || !kandaId) {
      setJumuiaList([]);
      return;
    }

    setLoading(true);
    try {
      const res = await getJumuia(effectiveParokiaId, kandaId);
      setJumuiaList(res.data || []);
    } catch (err) {
      console.error("Failed to load Jumuia", err);
      toast.error("Failed to load Jumuia");
      setJumuiaList([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!selectedParokia) return;

    loadKanda(selectedParokia);
    setSelectedKanda("");
    setJumuiaList([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedParokia]);

  useEffect(() => {
    if (!selectedParokia || !selectedKanda) return;

    loadJumuiaList({
      parokiaId: selectedParokia,
      kandaId: selectedKanda,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedKanda]);

  const handleCreate = async () => {
    if (!isParokiaAdmin) {
      toast.error("Only Parokia Admin can create Jumuia");
      return;
    }

    const pid = String(user?.parokiaId || "");
    if (!pid) {
      toast.error("No parokia assigned");
      return;
    }

    if (!selectedKanda) {
      toast.error("Please select Kanda (Jumuia must belong to Kanda)");
      return;
    }

    if (!form.name.trim()) {
      toast.error("Jumuia name is required");
      return;
    }

    if (!window.confirm("Create this Jumuia?")) return;

    try {
      await createJumuia({
        parokiaId: Number(pid),
        kandaId: Number(selectedKanda),
        name: form.name.trim(),
        leaderName: form.leaderName?.trim() || null,
        mwenyekiti: form.mwenyekiti?.trim() || null,
        katibu: form.katibu?.trim() || null,
        mweka_hazina: form.mweka_hazina?.trim() || null,
        status: form.status || "ACTIVE",
      });

      toast.success("Jumuia created successfully");
      setCreateOpen(false);
      setForm(initialForm);

      await loadJumuiaList({
        parokiaId: pid,
        kandaId: selectedKanda,
      });
    } catch (err) {
      console.error("Failed to create Jumuia", err);
      toast.error(err?.response?.data?.message || "Failed to create Jumuia");
    }
  };

  const openView = (j) => {
    setSelectedJumuia(j);
    setForm({
      name: j.jumuia_name || j.name || "",
      leaderName: j.leader_name || "",
      mwenyekiti: j.mwenyekiti || "",
      katibu: j.katibu || "",
      mweka_hazina: j.mweka_hazina || "",
      status: j.status || "ACTIVE",
    });
    setEditMode(false);
    setViewOpen(true);
  };

  const handleUpdate = async () => {
    if (!selectedJumuia?.jumuia_id) return;

    if (!window.confirm("Save changes?")) return;

    try {
      await updateJumuia(selectedJumuia.jumuia_id, {
        parokiaId: Number(user?.parokiaId || selectedParokia),
        name: form.name.trim(),
        leaderName: form.leaderName?.trim() || null,
        mwenyekiti: form.mwenyekiti?.trim() || null,
        katibu: form.katibu?.trim() || null,
        mweka_hazina: form.mweka_hazina?.trim() || null,
        status: form.status || "ACTIVE",
      });

      toast.success("Jumuia updated successfully");
      setEditMode(false);
      setViewOpen(false);

      await loadJumuiaList({
        parokiaId: selectedParokia,
        kandaId: selectedKanda,
      });
    } catch (err) {
      console.error("Failed to update Jumuia", err);
      toast.error(err?.response?.data?.message || "Failed to update Jumuia");
    }
  };

  const closeCreateModal = () => {
    setCreateOpen(false);
    setForm(initialForm);
  };

  const closeViewModal = () => {
    setViewOpen(false);
    setEditMode(false);
    setSelectedJumuia(null);
    setForm(initialForm);
  };

  return (
    <div className="page-container">
      <div className="hierarchy-header">
        <div>
          <h1>Jumuiya Management</h1>
          <p className="subtitle">
            {isParokiaAdmin
              ? "Manage Jumuiya under your Parokia"
              : "View Jumuiya by selecting Jimbo → Parokia → Kanda"}
          </p>
        </div>

        {isParokiaAdmin && (
          <button className="btn primary" onClick={() => setCreateOpen(true)}>
            + New Jumuia
          </button>
        )}
      </div>

      <div className="card">
        {!isParokiaAdmin ? (
          <div className="form-row jimbo">
            <div>
              <FieldLabel>Jimbo</FieldLabel>
              <select
                className="input"
                value={selectedJimbo}
                onChange={(e) => {
                  const id = e.target.value;
                  setSelectedJimbo(id);
                  loadParokiaByJimbo(id);
                }}
                disabled={jimbosLoading}
              >
                <option value="">
                  {jimbosLoading ? "Loading Jimbos..." : "Select Jimbo"}
                </option>
                {jimbos.map((j) => (
                  <option key={j.jimbo_id} value={j.jimbo_id}>
                    {j.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <FieldLabel>Parokia</FieldLabel>
              <select
                className="input"
                value={selectedParokia}
                onChange={(e) => setSelectedParokia(e.target.value)}
                disabled={!selectedJimbo || parokiaLoading}
              >
                <option value="">
                  {parokiaLoading ? "Loading Parokia..." : "Select Parokia"}
                </option>
                {parokiaList.map((p) => (
                  <option key={p.parokia_id} value={p.parokia_id}>
                    {p.parokia_name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <FieldLabel>Kanda</FieldLabel>
              <select
                className="input"
                value={selectedKanda}
                onChange={(e) => setSelectedKanda(e.target.value)}
                disabled={!selectedParokia || kandaLoading}
              >
                <option value="">
                  {kandaLoading ? "Loading Kanda..." : "Select Kanda"}
                </option>
                {kandaList.map((k) => (
                  <option key={k.kanda_id} value={k.kanda_id}>
                    {k.kanda_name || k.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        ) : (
          <div className="form-row jimbo">
            <div>
              <FieldLabel>Jimbo</FieldLabel>
              <input
                className="input"
                readOnly
                value={viewContext.jimbo_name || "—"}
                placeholder="Jimbo"
              />
            </div>

            <div>
              <FieldLabel>Parokia</FieldLabel>
              <input
                className="input"
                readOnly
                value={viewContext.parokia_name || user?.parokiaName || "—"}
                placeholder="Parokia"
              />
            </div>

            <div>
              <FieldLabel>Kanda</FieldLabel>
              <select
                className="input"
                value={selectedKanda}
                onChange={(e) => setSelectedKanda(e.target.value)}
                disabled={!selectedParokia || kandaLoading}
              >
                <option value="">
                  {kandaLoading ? "Loading Kanda..." : "Select Kanda"}
                </option>
                {kandaList.map((k) => (
                  <option key={k.kanda_id} value={k.kanda_id}>
                    {k.kanda_name || k.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}

        <div style={{ marginTop: 10 }}>
          <span className="subtitle" style={{ margin: 0 }}>
            {!selectedKanda
              ? "Select Kanda to view Jumuiya (Jumuiya must belong to Kanda)."
              : selectedKandaObj
              ? `Showing Jumuiya for ${selectedKandaObj.kanda_name || selectedKandaObj.name}`
              : ""}
          </span>
        </div>
      </div>

      <div className="card">
        <div className="card-header between">
          <div>
            <h3 style={{ margin: 0 }}>Available Jumuiya</h3>
            <p className="subtitle" style={{ marginTop: 6 }}>
              {!selectedKanda ? "Please select Kanda to view Jumuiya" : "Jumuiya list"}
            </p>
          </div>
        </div>

        {loading ? (
          <div className="spinner" />
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th style={{ width: 50 }}>#</th>
                <th>Jumuiya</th>
                <th>Leader</th>
                <th>Mwenyekiti</th>
                <th>Katibu</th>
                <th>Mweka Hazina</th>
                <th>Parokia</th>
                <th>Kanda</th>
                <th>Created At</th>
                <th style={{ width: 110 }}>Status</th>
                <th style={{ width: 120 }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {!selectedKanda ? (
                <tr>
                  <td colSpan="11" className="empty">
                    Please select Kanda
                  </td>
                </tr>
              ) : jumuiaList.length === 0 ? (
                <tr>
                  <td colSpan="11" className="empty">
                    No Jumuia found
                  </td>
                </tr>
              ) : (
                jumuiaList.map((j, i) => (
                  <tr key={j.jumuia_id}>
                    <td>{i + 1}</td>
                    <td className="bold">{j.jumuia_name || j.name}</td>
                    <td>{j.leader_name || "-"}</td>
                    <td>{j.mwenyekiti || "-"}</td>
                    <td>{j.katibu || "-"}</td>
                    <td>{j.mweka_hazina || "-"}</td>
                    <td>
                      {j.parokia_name ||
                        viewContext.parokia_name ||
                        user?.parokiaName ||
                        "-"}
                    </td>
                    <td>
                      {j.kanda_name ||
                        selectedKandaObj?.kanda_name ||
                        selectedKandaObj?.name ||
                        "-"}
                    </td>
                    <td>{formatDate(j.created_at)}</td>
                    <td>
                      <span className="badge-success">{j.status}</span>
                    </td>
                    <td>
                      <button className="btn small" onClick={() => openView(j)}>
                        View
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>

      {createOpen && isParokiaAdmin && (
        <div className="modal-overlay">
          <div className="modal" style={{ width: "95%", maxWidth: "900px" }}>
            <div className="modal-close" onClick={closeCreateModal}>
              ✕
            </div>

            <h3>Create Jumuiya</h3>

            <div className="modal-section">
              <h4>Context</h4>
              <div className="form-grid">
                <div>
                  <FieldLabel>Jimbo</FieldLabel>
                  <input className="input" readOnly value={viewContext.jimbo_name || "—"} />
                </div>

                <div>
                  <FieldLabel>Parokia</FieldLabel>
                  <input
                    className="input"
                    readOnly
                    value={viewContext.parokia_name || user?.parokiaName || "—"}
                  />
                </div>

                <div>
                  <FieldLabel>Kanda (Required)</FieldLabel>
                  <select
                    className="input"
                    value={selectedKanda}
                    onChange={(e) => setSelectedKanda(e.target.value)}
                    disabled={kandaLoading}
                  >
                    <option value="">
                      {kandaLoading ? "Loading Kanda..." : "Select Kanda"}
                    </option>
                    {kandaList.map((k) => (
                      <option key={k.kanda_id} value={k.kanda_id}>
                        {k.kanda_name || k.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <div className="modal-section">
              <h4>Jumuiya Details</h4>
              <div className="form-grid">
                <div>
                  <FieldLabel>Jumuiya Name</FieldLabel>
                  <input
                    className="input"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="Enter Jumuiya name"
                  />
                </div>
{/* 
                <div>
                  <FieldLabel>Leader Name (Optional)</FieldLabel>
                  <input
                    className="input"
                    value={form.leaderName}
                    onChange={(e) => setForm({ ...form, leaderName: e.target.value })}
                    placeholder="Enter leader name"
                  />
                </div> */}

                <div>
                  <FieldLabel>Mwenyekiti</FieldLabel>
                  <input
                    className="input"
                    value={form.mwenyekiti}
                    onChange={(e) => setForm({ ...form, mwenyekiti: e.target.value })}
                    placeholder="Enter Mwenyekiti"
                  />
                </div>

                <div>
                  <FieldLabel>Katibu</FieldLabel>
                  <input
                    className="input"
                    value={form.katibu}
                    onChange={(e) => setForm({ ...form, katibu: e.target.value })}
                    placeholder="Enter Katibu"
                  />
                </div>

                <div>
                  <FieldLabel>Mweka Hazina</FieldLabel>
                  <input
                    className="input"
                    value={form.mweka_hazina}
                    onChange={(e) => setForm({ ...form, mweka_hazina: e.target.value })}
                    placeholder="Enter Mweka Hazina"
                  />
                </div>
              </div>
            </div>

            <div className="modal-actions">
              <button className="btn" onClick={closeCreateModal}>
                Cancel
              </button>
              <button className="btn primary" onClick={handleCreate}>
                Create
              </button>
            </div>
          </div>
        </div>
      )}

      {viewOpen && selectedJumuia && (
        <div className="modal-overlay">
          <div className="modal" style={{ width: "95%", maxWidth: "900px" }}>
            <div className="modal-close" onClick={closeViewModal}>
              ✕
            </div>

            <h3>{editMode ? "Edit Jumuiya" : selectedJumuia.jumuia_name}</h3>

            {!editMode ? (
              <>
                <div className="modal-section">
                  <h4>Jumuiya Information</h4>
                  <div className="form-grid">
                    <div><strong>Name:</strong> {selectedJumuia.jumuia_name || "-"}</div>
                    <div><strong>Leader:</strong> {selectedJumuia.leader_name || "-"}</div>
                    <div><strong>Mwenyekiti:</strong> {selectedJumuia.mwenyekiti || "-"}</div>
                    <div><strong>Katibu:</strong> {selectedJumuia.katibu || "-"}</div>
                    <div><strong>Mweka Hazina:</strong> {selectedJumuia.mweka_hazina || "-"}</div>
                    <div><strong>Status:</strong> {selectedJumuia.status || "-"}</div>
                    <div><strong>Parokia:</strong> {selectedJumuia.parokia_name || "-"}</div>
                    <div><strong>Kanda:</strong> {selectedJumuia.kanda_name || "-"}</div>
                  </div>
                </div>

                <div className="modal-actions">
                  {isParokiaAdmin && (
                    <button className="btn" onClick={() => setEditMode(true)}>
                      Edit
                    </button>
                  )}
                  <button className="btn" onClick={closeViewModal}>
                    Close
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="modal-section">
                  <h4>Edit Jumuiya</h4>
                  <div className="form-grid">
                    <div>
                      <FieldLabel>Jumuiya Name</FieldLabel>
                      <input
                        className="input"
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                      />
                    </div>

                    <div>
                      <FieldLabel>Leader Name</FieldLabel>
                      <input
                        className="input"
                        value={form.leaderName}
                        onChange={(e) => setForm({ ...form, leaderName: e.target.value })}
                      />
                    </div>

                    <div>
                      <FieldLabel>Mwenyekiti</FieldLabel>
                      <input
                        className="input"
                        value={form.mwenyekiti}
                        onChange={(e) => setForm({ ...form, mwenyekiti: e.target.value })}
                      />
                    </div>

                    <div>
                      <FieldLabel>Katibu</FieldLabel>
                      <input
                        className="input"
                        value={form.katibu}
                        onChange={(e) => setForm({ ...form, katibu: e.target.value })}
                      />
                    </div>

                    <div>
                      <FieldLabel>Mweka Hazina</FieldLabel>
                      <input
                        className="input"
                        value={form.mweka_hazina}
                        onChange={(e) => setForm({ ...form, mweka_hazina: e.target.value })}
                      />
                    </div>
                  </div>
                </div>

                <div className="modal-actions">
                  <button className="btn" onClick={closeViewModal}>
                    Cancel
                  </button>
                  <button className="btn primary" onClick={handleUpdate}>
                    Save Changes
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      <ToastContainer position="top-right" autoClose={3000} />
    </div>
  );
}